import React from 'react';
import { Page, Text, View, Document, StyleSheet, Image, Font } from '@react-pdf/renderer';

// Register Noto Sans Gujarati font
Font.register({
  family: 'Noto Sans Gujarati',
  src: '/fonts/Noto_Sans_Gujarati/NotoSansGujarati-VariableFont_wdth,wght.ttf',
});

// Register Noto Sans Devanagari font
Font.register({
  family: 'Noto Sans Devanagari',
  src: '/fonts/Noto_Sans_Devanagari/NotoSansDevanagari-VariableFont_wdth,wght.ttf',
});

// Create styles
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    paddingLeft: 50,
    paddingRight: 50,
    paddingBottom: 30,
    paddingTop: 30
  },
  section: {
    margin: 0,
    padding: 0,
    flexGrow: 1
  },
});

const ChiefComplaintsReport = ({ prescription, patient, followUpData }) => {
  const getFontFamily = (text) => {
    if (!text) return 'Helvetica';
    if (/[\u0A80-\u0AFF]/.test(text)) return 'Noto Sans Gujarati';
    if (/[\u0900-\u097F]/.test(text)) return 'Noto Sans Devanagari';
    return 'Helvetica';
  };
  const patientName = patient?.basicInfo?.name || '';
  const patientAge = patient?.basicInfo?.age || '';
  const patientSex = patient?.basicInfo?.sex || '';
  
  let reportDate = new Date().toLocaleDateString('en-GB');
  if (followUpData?.visitDate) {
    try {
      reportDate = new Date(followUpData.visitDate).toLocaleDateString('en-GB');
    } catch (e) {
      reportDate = followUpData.visitDate;
    }
  } else if (prescription?.prescriptionDate) {
    try {
      reportDate = new Date(prescription.prescriptionDate).toLocaleDateString('en-GB');
    } catch (e) {
      reportDate = prescription.prescriptionDate;
    }
  }

  const patientWeight = patient?.basicInfo?.weight || patient?.examination?.weight || '';
  const spo2 = followUpData?.SPO2 || patient?.examination?.spo2 || '';
  const pulse = followUpData?.PR || patient?.examination?.pulse || patient?.examination?.pr || '';
  const bp = followUpData?.BP || patient?.examination?.bp || '';
  const pefr = followUpData?.PEFR || patient?.examination?.pefr || '';
  const adviceText = followUpData?.advice || prescription?.additionalNotes || '';

  const PDFHeader = (
    <View style={{ flexDirection: 'column', width: '100%', marginBottom: 10 }}>
      {/* Top Row: Logos and Doctor Info */}
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginTop: -10, paddingBottom: 5 }}>
        {/* Left Logo */}
        <View style={{ width: 85, paddingBottom: 0 }}>
          <Image src={'/image_logo.jpg'} style={{ width: '100%', objectFit: 'contain' }} />
        </View>

        {/* Dr Eshita V Shah */}
        <View style={{ flexDirection: 'column', alignItems: 'flex-start', flex: 1.4, paddingLeft: 15 }}>
          <Text style={{ fontWeight: 800, fontSize: 12, letterSpacing: 0.5, marginBottom: 3 }}>DR. ESHITA V. SHAH</Text>
          <Text style={{ fontSize: 8, marginBottom: 1 }}>M.D (Pulmonary Medicine)</Text>
          <Text style={{ fontSize: 8, marginBottom: 1 }}>European Diploma in Respiratory Medicine</Text>
          <Text style={{ fontSize: 8, marginBottom: 1 }}>Fellowship - Interventional Pulmonology</Text>
          <Text style={{ fontSize: 8 }}>& Thoracic Oncology (New Delhi)</Text>
        </View>

        {/* Dr Vipul Shah */}
        <View style={{ flexDirection: 'column', alignItems: 'flex-end', flex: 1.6, paddingRight: 15 }}>
          <Text style={{ fontWeight: 800, fontSize: 12, letterSpacing: 0.5, marginBottom: 3 }}>DR. VIPUL SHAH</Text>
          <Text style={{ fontSize: 8, marginBottom: 1 }}>PhD (Allergy), Diploma in Allergy,</Text>
          <Text style={{ fontSize: 8, marginBottom: 1 }}>Asthma & Immunology (USA)</Text>
          <Text style={{ fontSize: 8, marginBottom: 1 }}>Member of British Society for Allergy</Text>
          <Text style={{ fontSize: 8 }}>& Clinical Immunology</Text>
        </View>

        {/* Right Logo */}
        <View style={{ width: 60, paddingBottom: 0 }}>
          <Image src={'/image_chest.jpg'} style={{ width: '100%', objectFit: 'contain' }} />
        </View>
      </View>
      {/* Divider */}
      <View style={{ height: 2, backgroundColor: '#8B0000', marginTop: 5, marginBottom: 6, width: '100%' }} />
    </View>
  );

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {PDFHeader}
        
        {/* Patient Info and Date */}
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 5 }}>
          <View style={{ flexDirection: 'column' }}>
            <Text style={{ fontSize: 11, marginBottom: 4 }}>Name: {patientName}</Text>
            <Text style={{ fontSize: 11, marginBottom: 4 }}>Age/Sex: {patientAge}{patientAge && patientSex ? '/' : ''}{patientSex}</Text>
            <Text style={{ fontSize: 11 }}>Weight: {patientWeight}</Text>
          </View>
          <View style={{ flexDirection: 'column', alignItems: 'flex-end' }}>
            <Text style={{ fontSize: 11 }}>Date: {reportDate}</Text>
          </View>
        </View>

        {/* Vitals */}
        <View style={{ marginTop: 25, marginBottom: 25 }}>
          <View style={{ borderBottom: '1px solid #000', alignSelf: 'flex-start', paddingBottom: 3, marginBottom: 10 }}>
            <Text style={{ fontSize: 13, fontWeight: 'bold' }}>Vitals</Text>
          </View>
          <View style={{ marginLeft: 10 }}>
            <View style={{ flexDirection: 'row', marginBottom: 4 }}>
              <View style={{ width: 40 }}><Text style={{ fontSize: 11 }}>SPO2</Text></View>
              <Text style={{ fontSize: 11 }}>-  {spo2}</Text>
            </View>
            <View style={{ flexDirection: 'row', marginBottom: 4 }}>
              <View style={{ width: 40 }}><Text style={{ fontSize: 11 }}>PR</Text></View>
              <Text style={{ fontSize: 11 }}>-  {pulse}</Text>
            </View>
            <View style={{ flexDirection: 'row', marginBottom: 4 }}>
              <View style={{ width: 40 }}><Text style={{ fontSize: 11 }}>BP</Text></View>
              <Text style={{ fontSize: 11 }}>-  {bp}</Text>
            </View>
            <View style={{ flexDirection: 'row', marginBottom: 4 }}>
              <View style={{ width: 40 }}><Text style={{ fontSize: 11 }}>PEFR</Text></View>
              <Text style={{ fontSize: 11 }}>-  {pefr}</Text>
            </View>
          </View>
        </View>

        {/* Chief Complaints */}
        <View style={{ marginBottom: followUpData?.followUpInstructions ? 20 : 40 }}>
          <View style={{ borderBottom: '1px solid #000', alignSelf: 'flex-start', paddingBottom: 3, marginBottom: 10 }}>
            <Text style={{ fontSize: 13, fontWeight: 'bold' }}>Chief Complaints :</Text>
          </View>
          {followUpData?.followUpInstructions ? (
            <Text style={{ fontSize: 11, lineHeight: 1.4, fontFamily: getFontFamily(followUpData.followUpInstructions) }}>{followUpData.followUpInstructions}</Text>
          ) : null}
        </View>

        {/* Main Content Area - Advice */}
        <View style={[styles.section, { justifyContent: 'flex-end' }]}>
          {adviceText ? (
            <View>
              <View style={{ borderBottom: '1px solid #000', alignSelf: 'flex-start', paddingBottom: 3, marginBottom: 5 }}>
                <Text style={{ fontSize: 13, fontWeight: 'bold' }}>Advice</Text>
              </View>
              <Text style={{ fontSize: 11, lineHeight: 1.4, fontFamily: getFontFamily(adviceText) }}>{adviceText}</Text>
            </View>
          ) : (
            <View>
              <View style={{ borderBottom: '1px solid #000', alignSelf: 'flex-start', paddingBottom: 3, marginBottom: 5 }}>
                <Text style={{ fontSize: 13, fontWeight: 'bold' }}>Advice</Text>
              </View>
            </View>
          )}
        </View>

        {/* Signature Section */}
        <View style={{ marginTop: 25, width: '100%', paddingHorizontal: 5 }} wrap={false}>
          {/* Dedicated space for physical signature / stamp */}
          <View style={{ height: 45 }} />

          {/* Doctor Names Row */}
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', width: '100%' }}>
            <View style={{ width: '50%', alignItems: 'flex-start' }}>
              <Text style={{ fontWeight: 'bold', fontSize: 12, color: '#000000' }}>DR. ESHITA SHAH</Text>
            </View>
            <View style={{ width: '50%', alignItems: 'flex-end' }}>
              <Text style={{ fontWeight: 'bold', fontSize: 12, color: '#000000', textAlign: 'right' }}>DR. VIPUL SHAH</Text>
            </View>
          </View>

          {/* Patient Query Helpline */}
          <View style={{ marginTop: 14, borderTop: '1px solid #ddd', paddingTop: 8, alignItems: 'center' }}>
            <Text style={{ fontSize: 10, fontWeight: 'bold', color: '#8B0000', textAlign: 'center' }}>
              For any queries, please contact: +91 93166 33039
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default ChiefComplaintsReport;
